import { useEffect, useMemo, useState } from 'react'
import type { Contrato, ContratoTipo } from '@services/db.js'
import type { ContatoItem } from '@hooks/useContatos.js'
import { gerarParcelas, type NovaParcela, type NovoContrato } from '@services/gestao.js'
import { Modal } from '@components/primitives/Modal.js'
import { Button } from '@components/primitives/Button.js'
import { Input } from '@components/primitives/Input.js'
import { MoneyInput } from '@components/primitives/MoneyInput.js'
import { Select } from '@components/primitives/Select.js'
import { Textarea } from '@components/primitives/Textarea.js'
import { Alert } from '@components/primitives/Alert.js'
import { CONTRATO_TIPOS, FASES_CONSTRUTIVAS, SINAL_MAXIMO_PCT } from '@utils/constants.js'
import { formatCurrency } from '@utils/format.js'
import { CONDICAO_LABEL } from './contratosLabels.js'

interface Props {
  open: boolean
  initial?: Contrato | null
  contatos: ContatoItem[]
  onClose: () => void
  onSalvar: (input: NovoContrato, parcelas: NovaParcela[]) => Promise<void>
  onEditar?: (id: number, patch: Partial<NovoContrato>) => Promise<void>
  onNovoContato?: () => void
}

const NOVO_NOME = '__nome__'

const EMPTY = {
  titulo: '',
  contatoId: '',
  prestadorNome: '',
  tipo: 'empreitada-medicao' as ContratoTipo,
  valorTotal: 0,
  faseConstrutiva: '',
  dataInicio: '',
  dataFimPrevista: '',
  escopo: '',
  sinalPct: 15,
  retencaoPct: 10,
  marcos: ['Primeira medição', 'Segunda medição', 'Serviço concluído'],
}

export function ContratoFormModal({
  open,
  initial,
  contatos,
  onClose,
  onSalvar,
  onEditar,
  onNovoContato,
}: Props) {
  const [form, setForm] = useState(EMPTY)
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    if (!open) return
    if (initial) {
      setForm({
        ...EMPTY,
        titulo: initial.titulo,
        contatoId: initial.contatoId != null ? String(initial.contatoId) : NOVO_NOME,
        prestadorNome: initial.prestador,
        tipo: initial.tipo,
        valorTotal: initial.valorTotal,
        faseConstrutiva: initial.faseConstrutiva ?? '',
        dataInicio: initial.dataInicio ?? '',
        dataFimPrevista: initial.dataFimPrevista ?? '',
        escopo: initial.escopo ?? '',
        retencaoPct: initial.retencaoPct ?? 0,
      })
    } else {
      setForm({ ...EMPTY, contatoId: contatos.length ? '' : NOVO_NOME })
    }
  }, [open, initial, contatos.length])

  const set = <K extends keyof typeof EMPTY>(k: K, v: (typeof EMPTY)[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const prestadorSelecionado = contatos.find((c) => String(c.id) === form.contatoId)
  const prestador =
    form.contatoId === NOVO_NOME ? form.prestadorNome.trim() : (prestadorSelecionado?.nome ?? '')

  const parcelasPreview = useMemo(
    () =>
      initial
        ? []
        : gerarParcelas({
            valorTotal: form.valorTotal,
            sinalPct: form.sinalPct,
            retencaoPct: form.retencaoPct,
            marcos: form.marcos,
            ...(form.dataInicio ? { dataInicio: form.dataInicio } : {}),
            ...(form.dataFimPrevista ? { dataFim: form.dataFimPrevista } : {}),
          }),
    [form, initial],
  )

  const sinalAlto = form.sinalPct > SINAL_MAXIMO_PCT
  const canSubmit = form.titulo.trim().length > 1 && prestador.length > 1 && form.valorTotal > 0

  const contatoOptions = [
    ...(contatos.length ? [{ value: '', label: 'Selecione um contato' }] : []),
    ...contatos.map((c) => ({ value: String(c.id), label: `${c.nome} · ${c.papelLabel}` })),
    { value: NOVO_NOME, label: 'Digitar nome (sem cadastro)' },
  ]

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault()
    if (!canSubmit) return
    setSalvando(true)
    const base: NovoContrato = {
      titulo: form.titulo.trim(),
      prestador,
      tipo: form.tipo,
      valorTotal: form.valorTotal,
      status: initial?.status ?? 'ativo',
      ...(prestadorSelecionado ? { contatoId: prestadorSelecionado.id } : {}),
      ...(form.faseConstrutiva ? { faseConstrutiva: form.faseConstrutiva } : {}),
      ...(form.dataInicio ? { dataInicio: form.dataInicio } : {}),
      ...(form.dataFimPrevista ? { dataFimPrevista: form.dataFimPrevista } : {}),
      ...(form.escopo.trim() ? { escopo: form.escopo.trim() } : {}),
      ...(form.retencaoPct > 0 ? { retencaoPct: form.retencaoPct } : {}),
    }
    if (initial && onEditar) {
      await onEditar(initial.id!, {
        ...base,
        contatoId: prestadorSelecionado?.id,
        faseConstrutiva: form.faseConstrutiva || undefined,
        dataInicio: form.dataInicio || undefined,
        dataFimPrevista: form.dataFimPrevista || undefined,
        escopo: form.escopo.trim() || undefined,
      })
    } else {
      await onSalvar(base, parcelasPreview)
    }
    setSalvando(false)
    onClose()
  }

  return (
    <Modal
      open={open}
      title={initial ? 'Editar contrato' : 'Novo contrato'}
      size='lg'
      onClose={onClose}
      actions={
        <div className='cf3-modal-actions'>
          <Button variant='ghost' onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant='primary'
            type='submit'
            form='contrato-form'
            loading={salvando}
            disabled={!canSubmit}
          >
            {initial ? 'Salvar' : 'Criar contrato'}
          </Button>
        </div>
      }
    >
      <form id='contrato-form' className='cf3-gestao-form' onSubmit={handleSubmit}>
        <Input
          label='Serviço contratado'
          value={form.titulo}
          placeholder='Ex.: Alvenaria e reboco do térreo'
          required
          onChange={(e) => set('titulo', e.target.value)}
        />

        <Select
          label='Prestador'
          value={form.contatoId}
          options={contatoOptions}
          onChange={(e) => set('contatoId', e.target.value)}
        />
        {form.contatoId === NOVO_NOME && (
          <Input
            label='Nome do prestador'
            value={form.prestadorNome}
            placeholder='Ex.: Seu João'
            required
            onChange={(e) => set('prestadorNome', e.target.value)}
          />
        )}
        {onNovoContato && !initial && (
          <button type='button' className='cf3-gestao-form__link' onClick={onNovoContato}>
            Cadastrar novo contato
          </button>
        )}

        <div className='cf3-gestao-form__row'>
          <Select
            label='Forma de contratação'
            value={form.tipo}
            options={CONTRATO_TIPOS}
            onChange={(e) => set('tipo', e.target.value as ContratoTipo)}
          />
          <MoneyInput
            label='Valor total'
            value={form.valorTotal}
            required
            onChange={(v) => set('valorTotal', v)}
          />
        </div>

        <div className='cf3-gestao-form__row'>
          <Input
            label='Início previsto'
            type='date'
            value={form.dataInicio}
            onChange={(e) => set('dataInicio', e.target.value)}
          />
          <Input
            label='Fim previsto'
            type='date'
            value={form.dataFimPrevista}
            onChange={(e) => set('dataFimPrevista', e.target.value)}
          />
        </div>

        <Select
          label='Fase da obra'
          value={form.faseConstrutiva}
          options={[
            { value: '', label: 'Não definida' },
            ...FASES_CONSTRUTIVAS.map((f) => ({ value: f.id, label: f.label })),
          ]}
          onChange={(e) => set('faseConstrutiva', e.target.value)}
        />

        <Textarea
          label='Escopo combinado'
          value={form.escopo}
          rows={3}
          placeholder='O que está incluso, quem fornece material, quem tira entulho, padrão de acabamento…'
          hint='Escopo escrito evita "isso não estava no combinado".'
          onChange={(e) => set('escopo', e.target.value)}
        />

        {!initial && (
          <fieldset className='cf3-gestao-fieldset'>
            <legend className='cf3-gestao-fieldset__legend'>Parcelas por marco</legend>
            <p className='cf3-gestao-fieldset__desc'>
              Pague por serviço medido, nunca por data. Sinal pequeno, marcos claros e uma retenção
              até a vistoria final.
            </p>

            <div className='cf3-gestao-form__row'>
              <Input
                label='Sinal (%)'
                type='number'
                value={String(form.sinalPct)}
                onChange={(e) => set('sinalPct', clampPct(e.target.value))}
                {...(sinalAlto ? { error: `Acima de ${SINAL_MAXIMO_PCT}%: risco alto` } : {})}
              />
              <Input
                label='Retenção final (%)'
                type='number'
                value={String(form.retencaoPct)}
                onChange={(e) => set('retencaoPct', clampPct(e.target.value))}
              />
            </div>

            <div className='cf3-marcos'>
              <span className='cf-field__label'>Marcos de medição</span>
              {form.marcos.map((m, i) => (
                <div key={i} className='cf3-marcos__row'>
                  <input
                    className='cf-input'
                    value={m}
                    aria-label={`Marco ${i + 1}`}
                    placeholder={`Marco ${i + 1}`}
                    onChange={(e) => {
                      const marcos = [...form.marcos]
                      marcos[i] = e.target.value
                      set('marcos', marcos)
                    }}
                  />
                  <button
                    type='button'
                    className='cf3-marcos__remove'
                    aria-label={`Remover marco ${i + 1}`}
                    disabled={form.marcos.length <= 1}
                    onClick={() =>
                      set(
                        'marcos',
                        form.marcos.filter((_, j) => j !== i),
                      )
                    }
                  >
                    ×
                  </button>
                </div>
              ))}
              <Button
                variant='ghost'
                size='sm'
                disabled={form.marcos.length >= 8}
                onClick={() => set('marcos', [...form.marcos, ''])}
              >
                Adicionar marco
              </Button>
            </div>

            {sinalAlto && (
              <Alert
                variant='warning'
                title='Sinal alto'
                message={`Nunca pague mais de ${SINAL_MAXIMO_PCT}% antes do início. Sinal de 10–15% cobre a mobilização.`}
              />
            )}

            {parcelasPreview.length > 0 && (
              <ol className='cf3-parcelas-preview' aria-label='Parcelas geradas'>
                {parcelasPreview.map((p) => (
                  <li key={p.ordem} className='cf3-parcelas-preview__item'>
                    <span className='cf3-parcelas-preview__desc'>
                      {p.descricao}
                      <span className='cf3-parcelas-preview__cond'>
                        {' '}
                        · {CONDICAO_LABEL[p.condicao]}
                      </span>
                    </span>
                    <span className='cf3-parcelas-preview__valor'>{formatCurrency(p.valor)}</span>
                  </li>
                ))}
              </ol>
            )}
          </fieldset>
        )}
      </form>
    </Modal>
  )
}

function clampPct(raw: string): number {
  const n = Number(raw)
  if (!Number.isFinite(n)) return 0
  return Math.min(Math.max(Math.round(n), 0), 100)
}
